import { useGetPondNames } from 'modules/pond/api/queries'
import { useSpeciesId, useSpeciesName } from 'modules/pond/api/selector'
import PondDialog from 'modules/pond/components/PondDialog'
import { POND_MENU_ITEMS } from 'modules/pond/constants/navigation'
import { selectedCycleIdState } from 'modules/pond/stores/atom'
import { useAllowPondTabs } from 'modules/user/api/selector'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useLocation } from 'react-router'
import { useResetRecoilState } from 'recoil'
import { PATH_DASHBOARD } from 'routes/paths'

import {
  Box,
  Divider,
  Typography,
  FormControl,
  Select,
  MenuItem,
  ListItemIcon,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  SelectChangeEvent,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'

import { Icon } from '@iconify/react'

interface PondMenuProps {
  open: boolean
}

const PondMenu: React.FC<PondMenuProps> = ({ open }) => {
  const theme = useTheme()
  const { t } = useTranslation('layout')
  const speciesName = useSpeciesName()
  const speciesId = useSpeciesId()
  const { farmId } = useParams()
  const { pondId } = useParams()
  const location = useLocation()
  const [pondAnchorEl, setPondAnchorEl] = React.useState<null | HTMLElement>(null)
  const resetSelectedCycleIdState = useResetRecoilState(selectedCycleIdState)
  const { data: pondsData, isLoading } = useGetPondNames()
  const [openAddPondModal, setOpenAddPondModal] = React.useState(false)
  const navigate = useNavigate()
  const allowedFeatures = useAllowPondTabs()

  const [selectedPond, setSelectedPond] = React.useState<string>(pondId || 'all')

  const isMenuItemActive = React.useCallback(
    (path?: (farmId: string, pondId: string) => string) => {
      if (!path || !farmId || !pondId) return false
      const itemPath = path(farmId, pondId)
      return location.pathname === itemPath
    },
    [location.pathname, farmId, pondId]
  )

  const handleSelectPond = (eventOrValue: SelectChangeEvent<string> | string) => {
    let pondId: string

    if (typeof eventOrValue === 'string') {
      pondId = eventOrValue
    } else {
      pondId = eventOrValue.target.value
    }

    if (pondId === '__add__') {
      setOpenAddPondModal(true)
      return
    }

    if (pondId === 'all' || !pondId) {
      setSelectedPond('all')
      if (farmId) navigate(PATH_DASHBOARD.farms.detail(farmId))
      return
    }

    setSelectedPond(pondId)
    if (farmId) {
      const currentPath = location.pathname
      const pondPathRegex = /\/pond\/[^/]+(.*)$/
      const match = currentPath.match(pondPathRegex)
      const tabPath = match && match[1] ? match[1] : ''

      resetSelectedCycleIdState()
      navigate(PATH_DASHBOARD.ponds.detail(farmId, pondId) + tabPath)
    }
  }

  const handlePondIconClick = (event: React.MouseEvent<HTMLElement>) => {
    setPondAnchorEl(event.currentTarget)
  }
  const handlePondPopoverClose = () => setPondAnchorEl(null)

  const handleMenuItemClick = (path?: (farmId: string, pondId: string) => string) => {
    if (path && farmId && pondId) {
      navigate(path(farmId, pondId))
    }
  }

  React.useEffect(() => {
    if (pondId) setSelectedPond(pondId)
    else setSelectedPond('all')
  }, [pondId])

  return (
    <>
      <Divider />
      <Box mt={2} mb={2} mx={2}>
        <Typography
          variant="subtitle2"
          color="text.secondary"
          mb={1}
          sx={{ opacity: open ? 1 : 0, transition: 'opacity 0.2s' }}
        >
          {t('allPonds')}
        </Typography>
        {open ? (
          <>
            <FormControl fullWidth size="small">
              <Select
                disabled={isLoading}
                value={selectedPond}
                displayEmpty
                onChange={handleSelectPond}
                startAdornment={
                  <Box sx={{ mr: 2 }} display={'flex'} alignItems="center">
                    <img src="/static/icons/layout/pond.svg" width={38} height={38} />
                  </Box>
                }
                sx={{ borderRadius: 1 }}
              >
                <MenuItem value="all">{t('allPonds')}</MenuItem>
                {pondsData?.map((pond) => (
                  <MenuItem value={pond.id} key={pond.id}>
                    {pond.name}
                  </MenuItem>
                ))}
                <Divider />
                <MenuItem
                  value="__add__"
                  sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Icon icon="mdi:plus-box-outline" width={20} height={20} />
                  </ListItemIcon>
                  {t('addPond')}
                </MenuItem>
              </Select>
            </FormControl>
          </>
        ) : (
          <>
            <IconButton
              disabled={isLoading}
              onClick={handlePondIconClick}
              sx={{
                borderRadius: 1,
                p: 1,
                border: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img src="/static/icons/layout/pond.svg" width={25} height={25} />
            </IconButton>
            <Popover
              open={Boolean(pondAnchorEl)}
              anchorEl={pondAnchorEl}
              onClose={handlePondPopoverClose}
              anchorOrigin={{
                vertical: 'center',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'center',
                horizontal: 'left',
              }}
              PaperProps={{
                sx: { ml: 3, p: 0, minWidth: 200 },
              }}
            >
              <Box>
                <List disablePadding>
                  <MenuItem
                    onClick={() => {
                      handleSelectPond('all')
                      handlePondPopoverClose()
                    }}
                  >
                    {t('allPonds')}
                  </MenuItem>

                  {pondsData?.map((pond) => (
                    <ListItem
                      button
                      key={pond.id}
                      selected={selectedPond === pond.id}
                      onClick={() => {
                        handleSelectPond(pond.id)
                        handlePondPopoverClose()
                      }}
                      sx={{
                        px: 2,
                        py: 1.2,
                        '&.Mui-selected': { bgcolor: 'action.selected' },
                      }}
                    >
                      <ListItemText primary={pond.name} />
                    </ListItem>
                  ))}
                </List>
                <Divider />
                <ListItem
                  button
                  onClick={() => {
                    setOpenAddPondModal(true)
                    handlePondPopoverClose()
                  }}
                  sx={{ px: 2, py: 1.2, color: 'primary.main' }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Icon icon="mdi:plus-box-outline" width={20} height={20} />
                  </ListItemIcon>
                  <ListItemText primary={t('addPond', 'เพิ่มบ่อ')} />
                </ListItem>
              </Box>
            </Popover>
          </>
        )}
        {selectedPond !== 'all' && selectedPond !== 'add_pond' && (
          <List sx={{ mt: 1 }}>
            {POND_MENU_ITEMS.map((item) => {
              const hasPermission = allowedFeatures.some((feature) => feature === item.permission)
              if (!hasPermission) return null

              const isActive = isMenuItemActive(item.path)

              return (
                <ListItem disablePadding key={item.label}>
                  <ListItemButton
                    sx={{
                      justifyContent: open ? 'initial' : 'center',
                      px: 2.5,
                      bgcolor: isActive ? 'action.selected' : 'transparent',
                      '&:hover': {
                        bgcolor: isActive ? 'action.selected' : undefined,
                      },
                      borderLeft: isActive
                        ? `3px solid ${theme.palette.primary.main}`
                        : '3px solid transparent',
                    }}
                    onClick={() => handleMenuItemClick(item.path)}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 2 : 'auto',
                        justifyContent: 'center',
                        color: isActive ? 'primary.main' : 'inherit',
                      }}
                    >
                      <img src={item.icon(speciesId)} width={24} height={24} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t(item.label, { speciesName })}
                      sx={{
                        opacity: open ? 1 : 0,
                        transition: 'opacity 0.2s',
                        '& .MuiTypography-root': {
                          fontWeight: isActive ? 600 : 400,
                          color: isActive ? 'primary.main' : 'inherit',
                        },
                      }}
                    />
                    {item?.hasChevron && open && (
                      <Icon
                        icon="mdi:chevron-down"
                        width={20}
                        height={20}
                        style={{
                          marginLeft: 8,
                          color: isActive
                            ? theme.palette.primary.main
                            : theme.palette.text.secondary,
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              )
            })}
          </List>
        )}
      </Box>
      <PondDialog open={openAddPondModal} onClose={() => setOpenAddPondModal(false)} />
    </>
  )
}

export default PondMenu
